import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Button, Input, Select } from '@/shared/ui'
import { toast } from '@/shared/model/toast'
import type { AuthConfig, Condition, KV, SecretHint, Secrets, Variable } from '@/entities/integration'
import s from './integrations.module.css'

/* ------------------------------------------------------------ Drawer */

/** Боковая панель редактора: открывается справа, страница видна за ней. */
export function Drawer({
  title,
  subtitle,
  icon: _icon,
  onClose,
  footer,
  children,
  wide,
}: {
  title: string
  subtitle?: string
  icon?: ReactNode
  onClose: () => void
  footer?: ReactNode
  children: ReactNode
  wide?: boolean
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])

  return createPortal(
    <div className={s.drawerRoot} role="dialog" aria-modal="true" aria-label={title}>
      <div className={s.drawerBackdrop} onClick={onClose} />
      <aside className={`${s.drawer} ${wide ? s.drawerWide : ''}`}>
        <header className={s.drawerHeader}>
          
          <div className={s.drawerTitles}>
            <h2 className={s.drawerTitle}>{title}</h2>
            {subtitle && <p className={s.drawerSubtitle}>{subtitle}</p>}
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Закрыть">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
        </header>
        <div className={s.drawerBody}>{children}</div>
        {footer && <footer className={s.drawerFooter}>{footer}</footer>}
      </aside>
    </div>,
    document.body,
  )
}

/* ------------------------------------------------------------ Section */

/** Пронумерованный шаг редактора. */
export function Step({
  n: _n,
  title,
  hint,
  children,
  aside,
}: {
  n: number | string
  title: string
  hint?: ReactNode
  children: ReactNode
  aside?: ReactNode
}) {
  return (
    <section className={s.step}>
      <div className={s.stepHead}>
        <div className={s.stepTitles}>
          <h3 className={s.stepTitle}>{title}</h3>
          {hint && <p className={s.stepHint}>{hint}</p>}
        </div>
        {aside}
      </div>
      <div className={s.stepBody}>{children}</div>
    </section>
  )
}

export function Label({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className={s.label}>
      <span>{children}</span>
      {hint && <span className={s.labelHint}>{hint}</span>}
    </div>
  )
}

/* -------------------------------------------------------- Segmented */

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  size,
}: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: ReactNode; hint?: string }[]
  size?: 'sm'
}) {
  return (
    <div className={`${s.segmented} ${size === 'sm' ? s.segmentedSm : ''}`} role="radiogroup">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          title={o.hint}
          className={`${s.segment} ${value === o.value ? s.segmentActive : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/* ---------------------------------------------------------- Choice */

/** Крупная карточка выбора (тип интеграции, действие). */
export function ChoiceCard({
  active,
  onClick,
  icon,
  title,
  text,
  badge,
}: {
  active?: boolean
  onClick: () => void
  icon?: ReactNode
  title: string
  text?: string
  badge?: string
}) {
  return (
    <button type="button" className={`${s.choice} ${active ? s.choiceActive : ''}`} onClick={onClick}>
      {icon && <span className={s.choiceIcon}>{icon}</span>}
      <span className={s.choiceText}>
        <span className={s.choiceTitle}>
          {title}
          {badge && <span className={s.badgeBrand}>{badge}</span>}
        </span>
        {text && <span className={s.choiceDesc}>{text}</span>}
      </span>
      <span className={s.choiceRadio} aria-hidden="true" />
    </button>
  )
}

/* ----------------------------------------------------------- Toggle */

/** Шаг-переключатель Битрикс-конструктора и похожих мест. */
export function ToggleRow({
  on,
  onChange,
  title,
  text,
  icon,
  children,
  disabled,
  lockedHint,
}: {
  on: boolean
  onChange: (v: boolean) => void
  title: string
  text?: string
  icon?: ReactNode
  children?: ReactNode
  disabled?: boolean
  lockedHint?: string
}) {
  return (
    <div className={`${s.toggleRow} ${on ? s.toggleRowOn : ''}`}>
      <label className={s.toggleHead}>
        {icon && <span className={s.toggleIcon}>{icon}</span>}
        <span className={s.toggleText}>
          <span className={s.toggleTitle}>{title}</span>
          {text && <span className={s.toggleDesc}>{lockedHint && disabled ? lockedHint : text}</span>}
        </span>
        <span className={s.switch}>
          <input
            type="checkbox"
            checked={on}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span className={s.switchTrack}>
            <span className={s.switchThumb} />
          </span>
        </span>
      </label>
      {on && children && <div className={s.toggleBody}>{children}</div>}
    </div>
  )
}

/* ------------------------------------------------------------ Pills */

export function StatusPill({ status, children }: { status: string; children?: ReactNode }) {
  const map: Record<string, [string, string]> = {
    success: [s.pillOk ?? '', 'Доставлено'],
    ok: [s.pillOk ?? '', 'Готово'],
    failed: [s.pillBad ?? '', 'Ошибка'],
    error: [s.pillBad ?? '', 'Ошибка'],
    rejected: [s.pillBad ?? '', 'Отклонено'],
    retrying: [s.pillWarn ?? '', 'Повтор'],
    pending: [s.pillMuted ?? '', 'В очереди'],
    sending: [s.pillInfo ?? '', 'Отправляем'],
    accepted: [s.pillInfo ?? '', 'Принято'],
    partial: [s.pillWarn ?? '', 'Частично'],
    duplicate: [s.pillMuted ?? '', 'Повтор'],
    dry_run: [s.pillInfo ?? '', 'Проверка'],
    planned: [s.pillInfo ?? '', 'Сделаем'],
    skipped: [s.pillMuted ?? '', 'Пропуск'],
    paused: [s.pillWarn ?? '', 'Пауза'],
    off: [s.pillMuted ?? '', 'Выключено'],
    on: [s.pillOk ?? '', 'Работает'],
  }
  const [cls, label] = map[status] ?? [s.pillMuted ?? '', status]
  return (
    <span className={`${s.pill} ${cls}`}>
      <span className={s.pillDot} />
      {children ?? label}
    </span>
  )
}

/* ------------------------------------------------------- Copy field */

export function CopyField({ value, secret, label }: { value: string; secret?: boolean; label?: string }) {
  const [shown, setShown] = useState(!secret)
  const copy = () => {
    void navigator.clipboard?.writeText(value).then(
      () => toast.success(label ? `${label}: скопировано` : 'Скопировано'),
      () => toast.error('Не удалось скопировать — выделите и скопируйте вручную'),
    )
  }
  return (
    <div className={s.copyField}>
      <code className={s.copyValue}>{shown ? value : '•'.repeat(Math.min(32, value.length || 12))}</code>
      {secret && (
        <button type="button" className={s.iconBtn} onClick={() => setShown((v) => !v)} title={shown ? 'Скрыть' : 'Показать'}>
          {shown ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      )}
      <button type="button" className={s.copyBtn} onClick={copy}>
        <CopyIcon /> Копировать
      </button>
    </div>
  )
}

/* --------------------------------------------------------- JSON view */

export function JsonView({ value, maxHeight }: { value: unknown; maxHeight?: number }) {
  const text = useMemo(() => {
    if (typeof value === 'string') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2)
      } catch {
        return value
      }
    }
    return JSON.stringify(value, null, 2)
  }, [value])
  return (
    <pre className={s.code} style={maxHeight ? { maxHeight } : undefined}>
      {highlight(text)}
    </pre>
  )
}

/** Подсветка JSON без библиотек: ключи, строки, числа. */
function highlight(text: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /("(?:[^"\\]|\\.)*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index))
    if (m[1]) {
      out.push(
        <span key={i++} className={m[2] ? s.jKey : s.jStr}>
          {m[1]}
        </span>,
      )
      if (m[2]) out.push(m[2])
    } else if (m[3]) {
      out.push(
        <span key={i++} className={s.jLit}>
          {m[3]}
        </span>,
      )
    } else if (m[4]) {
      out.push(
        <span key={i++} className={s.jNum}>
          {m[4]}
        </span>,
      )
    }
    last = re.lastIndex
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/* ------------------------------------------------------ Tree picker */

/**
 * Дерево ответа: клиент «тычет мышкой» в поле. Показывает примеры
 * значений, массивы сворачиваются до первого элемента.
 */
export function TreePicker({
  data,
  selected,
  onPick,
  pickArrays,
  rootPath = '',
}: {
  data: unknown
  selected: string[]
  onPick: (path: string, sample: string, isArray: boolean) => void
  pickArrays?: boolean
  rootPath?: string
}) {
  return (
    <div className={s.tree}>
      <TreeNode value={data} path={rootPath} name={rootPath ? rootPath.split('.').pop() ?? '' : 'ответ'} depth={0}
        selected={selected} onPick={onPick} pickArrays={pickArrays} />
    </div>
  )
}

function TreeNode({
  value,
  path,
  name,
  depth,
  selected,
  onPick,
  pickArrays,
}: {
  value: unknown
  path: string
  name: string
  depth: number
  selected: string[]
  onPick: (path: string, sample: string, isArray: boolean) => void
  pickArrays?: boolean
}) {
  const [open, setOpen] = useState(depth < 2)
  const isArr = Array.isArray(value)
  const isObj = value !== null && typeof value === 'object' && !isArr
  const join = (k: string) => (path ? `${path}.${k}` : k)

  if (!isArr && !isObj) {
    const sample = value === null ? 'null' : String(value)
    const active = selected.includes(path)
    return (
      <button
        type="button"
        className={`${s.treeLeaf} ${active ? s.treeLeafActive : ''}`}
        style={{ paddingLeft: 10 + depth * 16 }}
        onClick={() => onPick(path, sample, false)}
        title={path}
      >
        <span className={s.treeCheck}>{active ? '✓' : '+'}</span>
        <span className={s.treeKey}>{name}</span>
        <span className={s.treeSample}>{sample.length > 60 ? sample.slice(0, 60) + '…' : sample}</span>
      </button>
    )
  }

  const entries: [string, unknown][] = isArr
    ? (value as unknown[]).length > 0
      ? [['[0]', (value as unknown[])[0]]]
      : []
    : Object.entries(value as Record<string, unknown>)
  const childPath = (k: string) => (isArr ? `${path}[0]` : join(k))
  const arrSelected = isArr && selected.includes(path)

  return (
    <div>
      <div className={s.treeBranch} style={{ paddingLeft: 10 + depth * 16 }}>
        <button type="button" className={s.treeToggle} onClick={() => setOpen((v) => !v)}>
          <span className={`${s.chev} ${open ? s.chevOpen : ''}`}>›</span>
          <span className={s.treeKey}>{name}</span>
          <span className={s.treeMeta}>
            {isArr ? `список · ${(value as unknown[]).length}` : `${entries.length} полей`}
          </span>
        </button>
        {isArr && pickArrays && (
          <button
            type="button"
            className={`${s.treeArrBtn} ${arrSelected ? s.treeArrBtnActive : ''}`}
            onClick={() => onPick(path, '', true)}
          >
            {arrSelected ? '✓ Это список записей' : 'Это список записей'}
          </button>
        )}
      </div>
      {open &&
        entries.map(([k, v]) => (
          <TreeNode key={k} value={v} path={isArr ? childPath(k) : join(k)} name={k} depth={depth + 1}
            selected={selected} onPick={onPick} pickArrays={pickArrays} />
        ))}
    </div>
  )
}

/* ------------------------------------------------------ Variables */

/** Кнопки-переменные: клик вставляет {{путь}} в поле. */
export function VariableChips({
  variables,
  onInsert,
  limit = 14,
}: {
  variables: Variable[]
  onInsert: (token: string) => void
  limit?: number
}) {
  const [all, setAll] = useState(false)
  const [q, setQ] = useState('')
  const list = variables.filter((v) => !q || v.path.toLowerCase().includes(q.toLowerCase()))
  const shown = all || q ? list : list.slice(0, limit)
  return (
    <div className={s.vars}>
      <div className={s.varsHead}>
        <span className={s.varsTitle}>Подставить значение</span>
        <input className={s.varsSearch} placeholder="Найти поле…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className={s.varsList}>
        {shown.map((v) => (
          <button
            key={v.path}
            type="button"
            className={s.varChip}
            title={v.sample ? `Пример: ${v.sample}` : v.path}
            onClick={() => onInsert(`{{${v.path}}}`)}
          >
            {prettyVar(v.path)}
          </button>
        ))}
        {!all && !q && list.length > limit && (
          <button type="button" className={s.varMore} onClick={() => setAll(true)}>
            ещё {list.length - limit}
          </button>
        )}
      </div>
    </div>
  )
}

const VAR_NAMES: Record<string, string> = {
  'data.contact.name': 'Имя клиента',
  'data.contact.phone': 'Телефон',
  'data.contact.labelTitle': 'Оценка клиента',
  'data.contact.summary': 'Резюме по клиенту',
  'data.contact.note': 'Заметка',
  'data.contact.cardUrl': 'Карточка клиента',
  'data.report.summary': 'Итог разговора',
  'data.report.outcomeTitle': 'Результат звонка',
  'data.report.score': 'Оценка звонка',
  'data.report.nextActionAt': 'Когда перезвонить',
  'data.report.nextActionNote': 'Что сделать дальше',
  'data.call.url': 'Ссылка на звонок',
  'data.call.recordingUrl': 'Запись',
  'data.call.durationSec': 'Длительность, сек',
  'data.call.startedAt': 'Начало звонка',
  'data.call.campaign.name': 'Обзвон',
  'data.call.bot.name': 'Бот',
  'data.campaign.name': 'Обзвон',
  'data.task.text': 'Текст задачи',
  'data.task.dueAt': 'Срок задачи',
  'company.name': 'Компания',
  type: 'Тип события',
  createdAt: 'Время события',
  'contact.phone': 'Телефон',
  'contact.phonePlus': 'Телефон с +',
  'contact.name': 'Имя клиента',
  'contact.note': 'Заметка',
  'contact.id': 'ID клиента',
  'campaign.name': 'Обзвон',
  today: 'Сегодня',
  now: 'Сейчас',
}

export function prettyVar(path: string): string {
  return VAR_NAMES[path] ?? path.replace(/^data\./, '')
}

/** Вставка в позицию курсора текстового поля. */
export function useInsert<T extends HTMLInputElement | HTMLTextAreaElement>(
  value: string,
  setValue: (v: string) => void,
) {
  const ref = useRef<T | null>(null)
  const insert = (token: string) => {
    const el = ref.current
    if (!el) {
      setValue(value + token)
      return
    }
    const start = el.selectionStart ?? value.length
    const end = el.selectionEnd ?? value.length
    const next = value.slice(0, start) + token + value.slice(end)
    setValue(next)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(start + token.length, start + token.length)
    })
  }
  return { ref, insert }
}

/* --------------------------------------------------------- KV editor */

export function KVEditor({
  items,
  onChange,
  keyPlaceholder = 'Заголовок',
  valuePlaceholder = 'Значение',
  addLabel = 'Добавить заголовок',
}: {
  items: KV[]
  onChange: (v: KV[]) => void
  keyPlaceholder?: string
  valuePlaceholder?: string
  addLabel?: string
}) {
  return (
    <div className={s.kv}>
      {items.map((kv, i) => (
        <div key={i} className={s.kvRow}>
          <Input value={kv.key} placeholder={keyPlaceholder}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} />
          <Input value={kv.value} placeholder={valuePlaceholder}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
          <button type="button" className={s.iconBtn} onClick={() => onChange(items.filter((_, j) => j !== i))}
            aria-label="Убрать">
            <TrashIcon />
          </button>
        </div>
      ))}
      <button type="button" className={s.addLink} onClick={() => onChange([...items, { key: '', value: '' }])}>
        + {addLabel}
      </button>
    </div>
  )
}

/* ----------------------------------------------------- Conditions */

export function ConditionsEditor({
  items,
  onChange,
  fields,
  operators,
  emptyText = 'Без условий — срабатывает всегда',
  presets,
}: {
  items: Condition[]
  onChange: (v: Condition[]) => void
  fields: { value: string; label: string }[]
  operators: { op: string; title: string; noArg: boolean }[]
  emptyText?: string
  presets?: { label: string; cond: Condition }[]
}) {
  const ops = operators.map((o) => ({ value: o.op, label: o.title }))
  const noArg = (op: string) => operators.find((o) => o.op === op)?.noArg
  return (
    <div className={s.conds}>
      {items.length === 0 && <div className={s.condEmpty}>{emptyText}</div>}
      {items.map((c, i) => (
        <div key={i} className={s.condRow}>
          <span className={s.condWord}>{i === 0 ? 'Если' : 'и'}</span>
          <Select
            options={withCurrent(fields, c.path)}
            value={c.path}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, path: e.target.value } : x)))}
          />
          <Select
            options={ops}
            value={c.op}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, op: e.target.value } : x)))}
          />
          {!noArg(c.op) ? (
            <Input
              value={c.value}
              placeholder="значение"
              onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
            />
          ) : (
            <span />
          )}
          <button type="button" className={s.iconBtn} onClick={() => onChange(items.filter((_, j) => j !== i))}
            aria-label="Убрать условие">
            <TrashIcon />
          </button>
        </div>
      ))}
      <div className={s.condActions}>
        <button
          type="button"
          className={s.addLink}
          onClick={() => onChange([...items, { path: fields[0]?.value ?? '', op: 'eq', value: '' }])}
        >
          + Добавить условие
        </button>
        {presets?.map((p) => (
          <button key={p.label} type="button" className={s.presetChip} onClick={() => onChange([...items, p.cond])}>
            {p.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function withCurrent(list: { value: string; label: string }[], cur: string) {
  if (!cur || list.some((x) => x.value === cur)) return list
  return [{ value: cur, label: cur }, ...list]
}

/* ------------------------------------------------------ Auth editor */

export const AUTH_META: Record<string, { title: string; text: string }> = {
  none: { title: 'Без авторизации', text: 'Открытый адрес или защита по IP' },
  header: { title: 'Ключ в заголовке', text: 'X-API-Key: ваш_ключ' },
  query: { title: 'Ключ в ссылке', text: '…?api_key=ваш_ключ' },
  bearer: { title: 'Bearer-токен', text: 'Authorization: Bearer …' },
  basic: { title: 'Логин и пароль', text: 'Basic Auth' },
  oauth_client: { title: 'OAuth 2.0 — сервер-сервер', text: 'client_id + client_secret, токен обновляем сами' },
  oauth_code: { title: 'OAuth 2.0 — вход через аккаунт', text: 'Кнопка «Подключить», как «Войти через Google»' },
  token_request: { title: 'Токен по запросу', text: 'Сначала получаем токен вашим запросом, потом ходим с ним' },
  hmac: { title: 'Подпись HMAC', text: 'Подписываем тело запроса общим секретом' },
  bitrix: { title: 'Битрикс24', text: 'Ссылка входящего вебхука портала' },
}

export function SecretInput({
  name,
  hint,
  value,
  onChange,
  placeholder,
}: {
  name: string
  hint?: SecretHint
  value: string | undefined
  onChange: (v: string) => void
  placeholder?: string
}) {
  const saved = hint?.set && value === undefined
  if (saved) {
    return (
      <div className={s.secretSaved}>
        <span className={s.secretLock}>
          <LockIcon />
        </span>
        <code>{hint?.hint || '••••••'}</code>
        <span className={s.secretNote}>хранится зашифрованным</span>
        <Button size="sm" variant="secondary" onClick={() => onChange('')}>
          Заменить
        </Button>
      </div>
    )
  }
  return (
    <Input
      type="password"
      autoComplete="new-password"
      name={name}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

export function AuthEditor({
  auth,
  setAuth,
  secrets,
  setSecret,
  hints,
  kinds,
  onConnect,
  hideBitrix,
}: {
  auth: AuthConfig
  setAuth: (a: AuthConfig) => void
  secrets: Secrets
  setSecret: (k: string, v: string) => void
  hints: Record<string, SecretHint>
  kinds: string[]
  onConnect?: () => void
  hideBitrix?: boolean
}) {
  const list = kinds.filter((k) => !(hideBitrix && k === 'bitrix'))
  const sec = (k: string, label: string, ph?: string) => (
    <div className={s.formRow}>
      <Label>{label}</Label>
      <SecretInput name={k} hint={hints[k]} value={secrets[k]} onChange={(v) => setSecret(k, v)} placeholder={ph} />
    </div>
  )
  const txt = (k: keyof AuthConfig, label: string, ph?: string, hint?: string) => (
    <div className={s.formRow}>
      <Label hint={hint}>{label}</Label>
      <Input value={(auth[k] as string) ?? ''} placeholder={ph}
        onChange={(e) => setAuth({ ...auth, [k]: e.target.value })} />
    </div>
  )
  return (
    <div className={s.authBox}>
      <div className={s.formRow}>
        <Label>Как ваш сервер проверяет, что это мы</Label>
        <Select
          value={auth.kind || 'none'}
          onChange={(e) => setAuth({ kind: e.target.value })}
          options={list.map((k) => ({ value: k, label: `${AUTH_META[k]?.title ?? k} — ${AUTH_META[k]?.text ?? ''}` }))}
        />
      </div>
      {auth.kind === 'header' && (
        <div className={s.grid2}>
          {txt('headerName', 'Название заголовка', 'X-API-Key')}
          {sec('value', 'Ключ', 'вставьте ключ')}
        </div>
      )}
      {auth.kind === 'query' && (
        <div className={s.grid2}>
          {txt('queryName', 'Название параметра', 'api_key')}
          {sec('value', 'Ключ', 'вставьте ключ')}
        </div>
      )}
      {auth.kind === 'bearer' && sec('value', 'Токен', 'вставьте токен')}
      {auth.kind === 'basic' && (
        <div className={s.grid2}>
          {txt('username', 'Логин')}
          {sec('password', 'Пароль')}
        </div>
      )}
      {(auth.kind === 'oauth_client' || auth.kind === 'oauth_code') && (
        <>
          {auth.kind === 'oauth_code' && txt('authorizeUrl', 'Адрес страницы входа', 'https://…/oauth/authorize')}
          {txt('tokenUrl', 'Адрес выдачи токена', 'https://…/oauth/token')}
          <div className={s.grid2}>
            {txt('clientId', 'client_id')}
            {sec('client_secret', 'client_secret')}
          </div>
          {txt('scope', 'Права (scope)', 'необязательно')}
          {auth.kind === 'oauth_code' && (
            <div className={s.connectBox}>
              {auth.connected ? (
                <StatusPill status="ok">Аккаунт подключён</StatusPill>
              ) : (
                <span className={s.muted}>Сохраните интеграцию и нажмите «Подключить» — откроется вход в вашу систему.</span>
              )}
              {onConnect && (
                <Button size="sm" variant={auth.connected ? 'secondary' : 'primary'} onClick={onConnect}>
                  {auth.connected ? 'Переподключить' : 'Подключить'}
                </Button>
              )}
            </div>
          )}
        </>
      )}
      {auth.kind === 'token_request' && (
        <>
          {txt('tokenUrl', 'Куда идти за токеном', 'https://api.example.ru/auth/login')}
          <div className={s.grid2}>
            <div className={s.formRow}>
              <Label>Формат</Label>
              <Select
                value={auth.tokenContentType || 'json'}
                onChange={(e) => setAuth({ ...auth, tokenContentType: e.target.value })}
                options={[
                  { value: 'json', label: 'JSON' },
                  { value: 'form', label: 'Форма (x-www-form-urlencoded)' },
                ]}
              />
            </div>
            {txt('tokenPath', 'Где токен в ответе', 'access_token', 'путь, например data.token')}
          </div>
          <div className={s.formRow}>
            <Label hint="{{secret.login}} и {{secret.password}} подставятся из полей ниже">Тело запроса</Label>
            <textarea
              className={s.textarea}
              rows={3}
              value={auth.tokenBody ?? '{"login": "{{secret.login}}", "password": "{{secret.password}}"}'}
              onChange={(e) => setAuth({ ...auth, tokenBody: e.target.value })}
            />
          </div>
          <div className={s.grid2}>
            {sec('login', 'Логин')}
            {sec('password', 'Пароль')}
          </div>
          <div className={s.grid2}>
            {txt('tokenHeader', 'Куда класть токен', 'Authorization')}
            {txt('tokenPrefix', 'Перед токеном', 'Bearer ')}
          </div>
        </>
      )}
      {auth.kind === 'hmac' && (
        <div className={s.grid2}>
          {txt('signatureHeader', 'Заголовок подписи', 'X-Signature')}
          {sec('hmac_secret', 'Общий секрет')}
        </div>
      )}
      {auth.kind === 'bitrix' && <BitrixUrlInput hint={hints.bitrix_url} value={secrets.bitrix_url} onChange={(v) => setSecret('bitrix_url', v)} />}
    </div>
  )
}

export function BitrixUrlInput({
  hint,
  value,
  onChange,
}: {
  hint?: SecretHint
  value: string | undefined
  onChange: (v: string) => void
}) {
  return (
    <div className={s.formRow}>
      <Label hint="Битрикс24 → Разработчикам → Другое → Входящий вебхук. Права: CRM, задачи, телефония, уведомления, пользователи.">
        Ссылка входящего вебхука
      </Label>
      <SecretInput
        name="bitrix_url"
        hint={hint}
        value={value}
        onChange={onChange}
        placeholder="https://ваш-портал.bitrix24.ru/rest/1/abc123def456/"
      />
    </div>
  )
}

/* ----------------------------------------------------------- Empty */

export function Empty({
  icon,
  title,
  text,
  action,
}: {
  icon: ReactNode
  title: string
  text: string
  action?: ReactNode
}) {
  return (
    <div className={s.empty}>
      <div className={s.emptyIcon}>{icon}</div>
      <h3 className={s.emptyTitle}>{title}</h3>
      <p className={s.emptyText}>{text}</p>
      {action}
    </div>
  )
}

/* ----------------------------------------------------------- Utils */

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const sec = Math.round((Date.now() - d.getTime()) / 1000)
  if (sec < 0) {
    const m = Math.round(-sec / 60)
    if (m < 60) return `через ${m} мин`
    return `в ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
  }
  if (sec < 45) return 'только что'
  if (sec < 3600) return `${Math.round(sec / 60)} мин назад`
  if (sec < 86400) return `${Math.round(sec / 3600)} ч назад`
  return d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function fullTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/** Секреты, которые пользователь реально менял (остальные не шлём). */
export function useSecrets() {
  const [secrets, setSecrets] = useState<Secrets>({})
  const setSecret = (k: string, v: string) => setSecrets((p) => ({ ...p, [k]: v }))
  return { secrets, setSecret, reset: () => setSecrets({}) }
}

/* ----------------------------------------------------------- Icons */

const I = ({ children, size = 18 }: { children: ReactNode; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
)

export const SendIcon = () => (
  <I>
    <path d="M17 3L8.5 11.5M17 3l-5.5 14-3-5.5L3 8.5 17 3z" />
  </I>
)
export const DataIcon = () => (
  <I>
    <ellipse cx="10" cy="5" rx="6" ry="2.5" />
    <path d="M4 5v10c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5V5M4 10c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5" />
  </I>
)
export const InboxIcon = () => (
  <I>
    <path d="M3 11l2-7h10l2 7v5H3v-5z" />
    <path d="M3 11h4l1 2h4l1-2h4" />
  </I>
)
export const LogIcon = () => (
  <I>
    <path d="M5 3h10v14H5z" />
    <path d="M8 7h4M8 10h4M8 13h2" />
  </I>
)
export const BoltIcon = () => (
  <I>
    <path d="M11 2L4 11h5l-1 7 7-9h-5l1-7z" />
  </I>
)
export const LinkIcon = () => (
  <I>
    <path d="M8.5 11.5a3 3 0 004.2 0l2.6-2.6a3 3 0 00-4.2-4.2l-.9.9" />
    <path d="M11.5 8.5a3 3 0 00-4.2 0l-2.6 2.6a3 3 0 004.2 4.2l.9-.9" />
  </I>
)
export const PlusIcon = () => (
  <I size={16}>
    <path d="M10 4v12M4 10h12" />
  </I>
)
export const TrashIcon = () => (
  <I size={16}>
    <path d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10" />
  </I>
)
export const CopyIcon = () => (
  <I size={15}>
    <rect x="7" y="7" width="9" height="9" rx="2" />
    <path d="M4 13V5a1 1 0 011-1h8" />
  </I>
)
export const EyeIcon = () => (
  <I size={16}>
    <path d="M2 10s3-5.5 8-5.5S18 10 18 10s-3 5.5-8 5.5S2 10 2 10z" />
    <circle cx="10" cy="10" r="2.3" />
  </I>
)
export const EyeOffIcon = () => (
  <I size={16}>
    <path d="M3 3l14 14M8.2 5a8 8 0 011.8-.5c5 0 8 5.5 8 5.5a14 14 0 01-2.3 2.9M5.4 6.6A13.6 13.6 0 002 10s3 5.5 8 5.5a7.4 7.4 0 003.3-.8" />
  </I>
)
export const LockIcon = () => (
  <I size={14}>
    <rect x="4.5" y="9" width="11" height="8" rx="2" />
    <path d="M7 9V6.5a3 3 0 016 0V9" />
  </I>
)
export const PlayIcon = () => (
  <I size={16}>
    <path d="M6 4l10 6-10 6V4z" />
  </I>
)
export const RetryIcon = () => (
  <I size={16}>
    <path d="M4 10a6 6 0 1 0 1.8-4.3M4 3v3.5h3.5" />
  </I>
)
export const CheckIcon = () => (
  <I size={16}>
    <path d="M4 10.5l4 4 8-9" />
  </I>
)
export const SearchIcon = () => (
  <I size={16}>
    <circle cx="9" cy="9" r="5.5" />
    <path d="M13 13l4 4" />
  </I>
)
export const UserIcon = () => (
  <I>
    <circle cx="10" cy="7" r="3.2" />
    <path d="M3.5 17c1-3.2 3.5-4.8 6.5-4.8s5.5 1.6 6.5 4.8" />
  </I>
)
export const StageIcon = () => (
  <I>
    <path d="M2.5 6h4l2 4-2 4h-4l2-4-2-4zM9.5 6h4l2 4-2 4h-4l2-4-2-4z" />
  </I>
)
export const FieldsIcon = () => (
  <I>
    <rect x="3" y="4" width="14" height="4" rx="1.2" />
    <rect x="3" y="12" width="14" height="4" rx="1.2" />
  </I>
)
export const CommentIcon = () => (
  <I>
    <path d="M4 4h12v9H9l-4 3v-3H4z" />
  </I>
)
export const PhoneIcon = () => (
  <I>
    <path d="M6.5 3.5l2 3.5-1.5 1.5a9 9 0 004.5 4.5L13 11.5l3.5 2-1 2.5c-6 .5-12-5.5-11.5-11.5l2.5-1z" />
  </I>
)
export const TodoIcon = () => (
  <I>
    <rect x="3.5" y="3.5" width="13" height="13" rx="3" />
    <path d="M7 10l2 2 4-4" />
  </I>
)
export const BellIcon = () => (
  <I>
    <path d="M5 13V9a5 5 0 0110 0v4l1.5 2h-13L5 13zM8.5 17a1.6 1.6 0 003 0" />
  </I>
)
export const RobotIcon = () => (
  <I>
    <rect x="4" y="6" width="12" height="10" rx="3" />
    <path d="M10 3v3M7.5 11h.01M12.5 11h.01" />
  </I>
)
export const FindIcon = () => (
  <I>
    <circle cx="8.5" cy="8.5" r="5" />
    <path d="M12.5 12.5L17 17" />
  </I>
)
export const NewIcon = () => (
  <I>
    <circle cx="10" cy="10" r="7" />
    <path d="M10 7v6M7 10h6" />
  </I>
)
export const BitrixLogo = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="11" fill="#2FC6F6" />
    <path d="M7.5 15.5V8.4h3.2c1.6 0 2.5.7 2.5 1.8 0 .7-.4 1.3-1.1 1.5.9.2 1.4.8 1.4 1.7 0 1.3-1 2.1-2.7 2.1H7.5zm1.7-4.2h1.3c.6 0 1-.3 1-.8s-.4-.8-1-.8H9.2v1.6zm0 2.9h1.5c.7 0 1.1-.3 1.1-.9 0-.5-.4-.9-1.1-.9H9.2v1.8zM15 15.5v-4.6h1.6v4.6H15zm.8-5.4a.9.9 0 110-1.8.9.9 0 010 1.8z"
      fill="#fff" />
  </svg>
)
